import {
  FIELD_SUB_TYPE,
  FIELD_TYPE,
  GENERIC_FIELD_TYPE,
} from '../../../../types'
import { getRawMessage } from '../../../../locale'

/**
 * fields with no logic available
 * META
 */

export const TEXTUAL_FIELDS = [
  FIELD_TYPE.SHORT_TEXT,
  FIELD_TYPE.LONG_TEXT,
  FIELD_TYPE.WEBSITE,
  FIELD_TYPE.EMAIL,
  FIELD_TYPE.PHONE,
  FIELD_TYPE.PHONE_VERIFICATION,
]

export const NUMERIC_FIELDS = [FIELD_TYPE.NUMBER, FIELD_TYPE.MONEY]

export const DATETIME_FIELDS = [FIELD_TYPE.DATE, FIELD_TYPE.TIME]

export const SELECT_FIELDS = [
  FIELD_TYPE.MATRIX,
  FIELD_TYPE.SINGLE_SELECT,
  FIELD_TYPE.YES_NO,
  FIELD_TYPE.RATING,
  FIELD_TYPE.DROPDOWN,
  FIELD_TYPE.MULTIPLE_SELECT,
]

export const BOOLEAN_FIELDS = [FIELD_TYPE.CHECKBOX]

export const FILE_UPLOADS = [FIELD_TYPE.FILE, FIELD_TYPE.SIGNATURE]

/**
 * Logic Condition Types
 * @enum {object}
 * @property {string} key
 * @property {string} displayName
 * @property {FIELD_TYPE[]} fieldTypes
 */
export const CONDITION_TYPE = {
  EQUAL: {
    key: 'equal',
    displayName: getRawMessage('logic.conditionTypes.equal'),
  },
  NOT_EQUAL: {
    key: 'not_equal',
    displayName: getRawMessage('logic.conditionTypes.not_equal'),
  },
  GREATER_THAN_OR_EQUAL: {
    key: 'gte',
    displayName: getRawMessage('logic.conditionTypes.greater_than_or_equal'),
  },
  LESS_THAN_OR_EQUAL: {
    key: 'lte',
    displayName: getRawMessage('logic.conditionTypes.less_than_or_equal'),
  },
  GREATER_THAN: {
    key: 'gt',
    displayName: getRawMessage('logic.conditionTypes.greater_than'),
  },
  LESS_THAN: {
    key: 'lt',
    displayName: getRawMessage('logic.conditionTypes.less_than'),
  },
  IS: {
    key: 'is',
    displayName: getRawMessage('logic.conditionTypes.is'),
  },
  IS_NOT: {
    key: 'is_not',
    displayName: getRawMessage('logic.conditionTypes.is_not'),
  },
  ON: {
    key: 'on',
    displayName: getRawMessage('logic.conditionTypes.on'),
  },
  NOT_ON: {
    key: 'not_on',
    displayName: getRawMessage('logic.conditionTypes.not_on'),
  },
  BEFORE: {
    key: 'before',
    displayName: getRawMessage('logic.conditionTypes.before'),
  },
  AFTER: {
    key: 'after',
    displayName: getRawMessage('logic.conditionTypes.after'),
  },
  BEFORE_OR_ON: {
    key: 'before_or_on',
    displayName: getRawMessage('logic.conditionTypes.before_or_on'),
  },
  AFTER_OR_ON: {
    key: 'after_or_on',
    displayName: getRawMessage('logic.conditionTypes.after_or_on'),
  },
  IS_ANSWERED: {
    key: 'is_answered',
    displayName: getRawMessage('logic.conditionTypes.is_answered'),
  },
  CONTAINS: {
    key: 'contains',
    displayName: getRawMessage('logic.conditionTypes.contains'),
  },
  NOT_CONTAINS: {
    key: 'not_contains',
    displayName: getRawMessage('logic.conditionTypes.not_contains'),
  },
  STARTS_WITH: {
    key: 'starts_with',
    displayName: getRawMessage('logic.conditionTypes.starts_with'),
  },
  ENDS_WITH: {
    key: 'ends_with',
    displayName: getRawMessage('logic.conditionTypes.ends_with'),
  },
  ALWAYS: {
    key: 'always',
    displayName: getRawMessage('logic.always'),
  },
  OTHERWISE: {
    key: 'otherwise',
    displayName: getRawMessage('logic.otherwise'),
  },
}

export const TEXTUAL_CONDITIONS = [
  CONDITION_TYPE.EQUAL,
  CONDITION_TYPE.NOT_EQUAL,
  CONDITION_TYPE.CONTAINS,
  CONDITION_TYPE.NOT_CONTAINS,
  CONDITION_TYPE.STARTS_WITH,
  CONDITION_TYPE.ENDS_WITH,
]

export const NUMERIC_CONDITIONS = [
  CONDITION_TYPE.EQUAL,
  CONDITION_TYPE.NOT_EQUAL,
  CONDITION_TYPE.GREATER_THAN,
  CONDITION_TYPE.LESS_THAN,
  CONDITION_TYPE.GREATER_THAN_OR_EQUAL,
  CONDITION_TYPE.LESS_THAN_OR_EQUAL,
]

export const DATETIME_CONDITIONS = [
  CONDITION_TYPE.ON,
  CONDITION_TYPE.NOT_ON,
  CONDITION_TYPE.AFTER,
  CONDITION_TYPE.BEFORE,
  CONDITION_TYPE.BEFORE_OR_ON,
  CONDITION_TYPE.AFTER_OR_ON,
]

export const SELECT_CONDITIONS = [CONDITION_TYPE.IS, CONDITION_TYPE.IS_NOT]

export const BOOLEAN_CONDITIONS = [
  CONDITION_TYPE.EQUAL,
  CONDITION_TYPE.NOT_EQUAL,
]

export const GENERAL_CONDITIONS = [CONDITION_TYPE.IS_ANSWERED]

export const FIELD_SEMANTIC_TYPE_CONDITIONS_DICT = {}

const COMMON_LOGIC_ACTIONS = {
  REDIRECT: {
    displayName: getRawMessage('logic.actionTypes.redirect'),
    key: 'redirect',
  },
  GOTO_SUCCESS_PAGE: {
    displayName: getRawMessage('logic.actionTypes.goToSuccessPage'),
    key: 'jump_to_success_page',
  },
}

const SINGLE_LOGIC_ACTIONS = {
  SHOW: {
    displayName: getRawMessage('logic.actionTypes.show'),
    key: 'show',
  },
  HIDE: {
    displayName: getRawMessage('logic.actionTypes.hide'),
    key: 'hide',
  },
}

const MULTI_STEP_LOGIC_ACTIONS = {
  JUMP: { displayName: getRawMessage('logic.actionTypes.jump'), key: 'jump' },
  SUBMIT: {
    displayName: getRawMessage('logic.actionTypes.submit'),
    key: 'submit',
  },
}

// CALCULATOR ACTIONS
const CALCULATOR_ACTIONS = {
  ADD: { displayName: getRawMessage('calculator.operand.plus'), key: 'add' },
  SUBTRACT: {
    displayName: getRawMessage('calculator.operand.subtract'),
    key: 'subtract',
  },
  MULTIPLY: {
    displayName: getRawMessage('calculator.operand.multiply'),
    key: 'multiply',
  },
  DIVIDE: {
    displayName: getRawMessage('calculator.operand.divide'),
    key: 'divide',
  },
}

/**
 * Logic Condition Argument
 * @typedef {Object} ConditionArg
 * @property {('field'|'choice'|'constants')} type
 * @property {string|number} value
 */

export const LOGIC_ACTIONS = {
  ...MULTI_STEP_LOGIC_ACTIONS,
  ...SINGLE_LOGIC_ACTIONS,
  ...COMMON_LOGIC_ACTIONS,
  ...CALCULATOR_ACTIONS,
}

export const FIELD_SEMANTIC_TYPE_DICT = {
  // TEXT
  [FIELD_TYPE.SHORT_TEXT]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.LONG_TEXT]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.WEBSITE]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.EMAIL]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.PHONE]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.PHONE_VERIFICATION]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...GENERAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.HIDDEN]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...TEXTUAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // NUMERIC
  [FIELD_TYPE.NUMBER]: {
    type: GENERIC_FIELD_TYPE.NUMBER,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.MONEY]: {
    type: GENERIC_FIELD_TYPE.NUMBER,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // value is a number, but it is a select input
  [`${FIELD_TYPE.RATING}.${FIELD_SUB_TYPE.RATING.SCORE}`]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [`${FIELD_TYPE.RATING}.${FIELD_SUB_TYPE.RATING.STAR}`]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [`${FIELD_TYPE.RATING}.${FIELD_SUB_TYPE.RATING.NPS}`]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [`${FIELD_TYPE.RATING}.${FIELD_SUB_TYPE.RATING.EMBEDDED}`]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...NUMERIC_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // SELECT
  [`${FIELD_TYPE.RATING}.${FIELD_SUB_TYPE.RATING.LIKE_DISLIKE}`]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    // 👇 because values are -1 & +1
    conditions: [...SELECT_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // it's a bit complex, so we'll just check general conditions
  // TODO: implement matrix conditions
  [FIELD_TYPE.MATRIX]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...GENERAL_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.SINGLE_SELECT]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...SELECT_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.YES_NO]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...SELECT_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.DROPDOWN]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...SELECT_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.MULTIPLE_SELECT]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: [...SELECT_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // BOOLEAN
  [FIELD_TYPE.CHECKBOX]: {
    type: GENERIC_FIELD_TYPE.BOOLEAN,
    conditions: [...BOOLEAN_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // FILE
  [FIELD_TYPE.FILE]: {
    type: GENERIC_FIELD_TYPE.FILE,
    conditions: GENERAL_CONDITIONS,
  },
  [FIELD_TYPE.SIGNATURE]: {
    type: GENERIC_FIELD_TYPE.FILE,
    conditions: GENERAL_CONDITIONS,
  },
  // DATETTIME
  [FIELD_TYPE.DATE]: {
    type: GENERIC_FIELD_TYPE.DATE,
    conditions: [...DATETIME_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  [FIELD_TYPE.TIME]: {
    type: GENERIC_FIELD_TYPE.TIME,
    conditions: [...DATETIME_CONDITIONS, ...GENERAL_CONDITIONS],
  },
  // COMPLEX TYPES
  [FIELD_TYPE.LOCATION]: {
    type: GENERIC_FIELD_TYPE.OBJECT,
    conditions: GENERAL_CONDITIONS,
  },
  [FIELD_TYPE.PRODUCT]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: GENERAL_CONDITIONS,
  },
  [FIELD_TYPE.CITY]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: GENERAL_CONDITIONS,
  },
  [FIELD_TYPE.TABLE]: {
    type: GENERIC_FIELD_TYPE.SELECT,
    conditions: GENERAL_CONDITIONS,
  },
  // VARIABLE
  [`${FIELD_TYPE.VARIABLE}.${FIELD_SUB_TYPE.VARIABLE.INT}`]: {
    type: GENERIC_FIELD_TYPE.NUMBER,
    conditions: NUMERIC_CONDITIONS,
  },
  [`${FIELD_TYPE.VARIABLE}.${FIELD_SUB_TYPE.VARIABLE.STRING}`]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: TEXTUAL_CONDITIONS,
  },
  [`${FIELD_TYPE.VARIABLE}.${FIELD_SUB_TYPE.VARIABLE.DECIMAL}`]: {
    type: GENERIC_FIELD_TYPE.NUMBER,
    conditions: NUMERIC_CONDITIONS,
  },
  [`${FIELD_TYPE.VARIABLE}.${FIELD_SUB_TYPE.VARIABLE.FORMULA}`]: {
    type: GENERIC_FIELD_TYPE.TEXT,
    conditions: [...NUMERIC_CONDITIONS, ...TEXTUAL_CONDITIONS],
  },
}
